const DESCRIPTION_LOCKED_STATUSES = [
  'project_won',
  'live_deal',
  'project_lost',
  'project_closed',
] as const;

/** crm_opportunity.slug is varchar(255) — must match OpportunityLiveDealRules::MAX_SLUG_LENGTH. */
export const OPPORTUNITY_SLUG_MAX_LENGTH = 255;

/** crm_opportunity.description — displayed as "Project name". */
export const OPPORTUNITY_PROJECT_NAME_MAX_LENGTH = 170;

/** crm_opportunity.detail — displayed as "Description". */
export const OPPORTUNITY_DETAIL_MAX_LENGTH = 500;

/**
 * Caps the generated opportunity slug at the DB column size.
 * The slug is built once at creation and never rewritten, so editing the project name
 * or the description leaves it untouched.
 */
export function truncateOpportunitySlug(slug: string): string {
  return (slug ?? '').slice(0, OPPORTUNITY_SLUG_MAX_LENGTH);
}

/** Closing date stays editable until the opportunity is won (project_won locks it). */
const CLOSING_DATE_LOCKED_STATUSES = [
  'project_won',
  'live_deal',
  'project_lost',
  'project_closed',
] as const;

/** CRM wizard: Billing Information step is editable for these statuses (see crm-opportunity-wizard mat-step). */
const CRM_BILLING_EDITABLE_STATUSES = [
  'awaiting_reply',
  'project_won',
  'live_deal',
] as const;

export type BillingEditContext = 'crm' | 'execution' | 'billing';

/** Live deal set to terminated (status project_closed / is_closed). */
export function isDealTerminated(opportunity: any): boolean {
  if (!opportunity) {
    return false;
  }

  const status = opportunity.slug_status ?? '';
  if (status === 'project_closed') {
    return true;
  }

  const isClosed = opportunity.is_closed;
  return (
    isClosed === true ||
    isClosed === 1 ||
    isClosed === '1' ||
    isClosed === 'true'
  );
}

/**
 * Statuses that render the compact command-bar deal header instead of the card grid.
 * Once a deal is live it belongs to execution, and once it is terminated it belongs to
 * nobody: in both cases the CRM opportunity wizard and the execution deal synopsis show
 * the SAME header for the same deal. Earlier statuses stay on the editable card grid.
 */
const COMMAND_BAR_HEADER_STATUSES = ['live_deal', 'project_closed'] as const;

/**
 * @see COMMAND_BAR_HEADER_STATUSES — the header is chosen by the deal status, never by
 * the page: aligning the two views of one deal is the whole point.
 */
export function usesCommandBarDealHeader(opportunity: any): boolean {
  if (!opportunity) {
    return false;
  }

  if (isDealTerminated(opportunity)) {
    return true;
  }

  return (COMMAND_BAR_HEADER_STATUSES as readonly string[]).includes(opportunity.slug_status ?? '');
}

/**
 * Deal-header actions (AFS roles, associated counterparty, deal team) are locked when
 * the page showing them does not own the deal:
 *   - terminated  -> locked everywhere, the deal is read-only;
 *   - live_deal   -> locked outside execution (CRM wizard, project billing), because a
 *                    live deal is managed from the deal synopsis;
 *   - anything else -> open, the CRM funnel still owns it.
 */
export function areDealHeaderActionsLocked(opportunity: any, isExecution: boolean = false): boolean {
  if (isDealTerminated(opportunity)) {
    return true;
  }

  if (isExecution) {
    return false;
  }

  return (opportunity?.slug_status ?? '') === 'live_deal';
}

/** Why a deal-header action is greyed out — shown as the tooltip of the disabled control. */
export function getDealActionsLockedMessage(opportunity: any, isExecution: boolean = false): string {
  if (!areDealHeaderActionsLocked(opportunity, isExecution)) {
    return '';
  }

  if (isDealTerminated(opportunity)) {
    return 'This deal is terminated. All deal data is read-only.';
  }

  return 'This deal is live. It is managed from the deal synopsis and is read-only here.';
}

/**
 * Re-opening a terminated deal (project_closed -> live_deal) is the ONE mutation
 * allowed on it, and it is superadmin-only.
 * Crm_opportunity::update_crm_opportunity enforces the same rule server-side.
 */
export function canReopenTerminatedDeal(opportunity: any, userGroups: string[] = []): boolean {
  if (!isDealTerminated(opportunity)) {
    return false;
  }

  return (userGroups ?? []).includes('superadmin');
}

function isLockedByStatus(opportunity: any, lockedStatuses: readonly string[]): boolean {
  if (!opportunity) {
    return true;
  }

  const status = opportunity.slug_status ?? '';
  if (lockedStatuses.includes(status)) {
    return true;
  }

  const isClosed = opportunity.is_closed;
  return (
    isClosed === true ||
    isClosed === 1 ||
    isClosed === '1' ||
    isClosed === 'true'
  );
}

/** Busdev in charge, busdev manager, or super admin — the actors allowed on restricted CRM fields. */
function isBusdevOwnerOrManager(
  opportunity: any,
  userSlug: string,
  userGroups: string[] = [],
): boolean {
  if (userGroups.includes('superadmin') || userGroups.includes('busdev_manager')) {
    return true;
  }

  const busdevSlug = opportunity?.slug_busdev_in_charge ?? '';
  return busdevSlug !== '' && userSlug === busdevSlug;
}

export function isOpportunityDescriptionLockedByStatus(opportunity: any): boolean {
  return isLockedByStatus(opportunity, DESCRIPTION_LOCKED_STATUSES);
}

export function isOpportunityClosingDateLockedByStatus(opportunity: any): boolean {
  return isLockedByStatus(opportunity, CLOSING_DATE_LOCKED_STATUSES);
}

/**
 * Merges in-progress form values for restricted-field permission checks on the create flow.
 * `opportunitypost.slug_busdev_in_charge` stays empty until save; the form uses `selectpersonincharge`.
 */
export function resolveOpportunityForRestrictedFieldEdit(
  opportunity: any,
  formBusdevSlug?: string | null,
  formStatusSlug?: string | null,
): any {
  if (!opportunity) {
    return opportunity;
  }

  return {
    ...opportunity,
    slug_busdev_in_charge: formBusdevSlug ?? opportunity.slug_busdev_in_charge ?? '',
    slug_status: formStatusSlug ?? opportunity.slug_status ?? '',
  };
}

/** @see resolveOpportunityForRestrictedFieldEdit — kept for the description call sites. */
export function resolveOpportunityForDescriptionEdit(
  opportunity: any,
  formBusdevSlug?: string | null,
  formStatusSlug?: string | null,
): any {
  return resolveOpportunityForRestrictedFieldEdit(opportunity, formBusdevSlug, formStatusSlug);
}

/**
 * Project name edition (DB column `description`, shown as "Project name" in the UI).
 * Allowed for the Busdev in charge, a Busdev manager, or a super admin, until the deal is won.
 */
export function canEditOpportunityDescription(
  opportunity: any,
  userSlug: string,
  userGroups: string[] = [],
): boolean {
  if (!opportunity || isOpportunityDescriptionLockedByStatus(opportunity)) {
    return false;
  }

  return isBusdevOwnerOrManager(opportunity, userSlug, userGroups);
}

/**
 * Description edition (DB column `detail`, shown as "Description" in the UI).
 * Same gate as the project name.
 */
export function canEditOpportunityDetail(
  opportunity: any,
  userSlug: string,
  userGroups: string[] = [],
): boolean {
  return canEditOpportunityDescription(opportunity, userSlug, userGroups);
}

/**
 * Free-text description (DB column `detail`) shown on the synopsis header.
 *
 * A live or terminated deal is past `DESCRIPTION_LOCKED_STATUSES`, so nobody can edit
 * it there and an empty value has nothing to offer: the block is dropped rather than
 * printing an "Add a description" placeholder that leads nowhere. Inside the CRM funnel
 * the block stays visible while empty, because it doubles as the edit entry point.
 */
export function shouldShowOpportunityDescription(
  opportunity: any,
  userSlug: string,
  userGroups: string[] = [],
): boolean {
  if ((opportunity?.detail ?? '').toString().trim() !== '') {
    return true;
  }

  return canEditOpportunityDetail(opportunity, userSlug, userGroups);
}

export function getRestrictedFieldReadOnlyMessage(
  opportunity: any,
  userSlug: string,
  userGroups: string[] = [],
  fieldLabel: string = 'project name',
): string {
  if (canEditOpportunityDescription(opportunity, userSlug, userGroups)) {
    return '';
  }

  if (isOpportunityDescriptionLockedByStatus(opportunity)) {
    return `The ${fieldLabel} can no longer be changed once the deal is won.`;
  }

  return `Only the Busdev in charge, a Busdev manager, or a super admin can change the ${fieldLabel}.`;
}

/**
 * Why a restricted opportunity text field (project name, description) is read-only, or ''
 * when it is editable. A CDE lock greys the whole opportunity form, project name included,
 * so its reason wins over the role / status one. The field state derives from this phrase:
 * a disabled field always has a tooltip that explains it.
 */
export function opportunityRestrictedFieldLockReason(
  cdeFormLockReason: string | null,
  fieldReadOnlyReason: string | null,
): string {
  const cdeReason = (cdeFormLockReason ?? '').trim();
  if (cdeReason) {
    return cdeReason;
  }

  return (fieldReadOnlyReason ?? '').trim();
}

/**
 * Closing date edition: allowed for the Busdev in charge, a Busdev manager, or a super admin,
 * as long as the opportunity has not reached project_won (nor live / lost / closed).
 */
export function canEditOpportunityClosingDate(
  opportunity: any,
  userSlug: string,
  userGroups: string[] = [],
): boolean {
  if (!opportunity || isOpportunityClosingDateLockedByStatus(opportunity)) {
    return false;
  }

  return isBusdevOwnerOrManager(opportunity, userSlug, userGroups);
}

export function getClosingDateReadOnlyMessage(
  opportunity: any,
  userSlug: string,
  userGroups: string[] = [],
): string {
  if (canEditOpportunityClosingDate(opportunity, userSlug, userGroups)) {
    return '';
  }

  if (isOpportunityClosingDateLockedByStatus(opportunity)) {
    return 'The closing date can no longer be changed once the deal is won.';
  }

  return 'Only the Busdev in charge, a Busdev manager, or a super admin can change the closing date.';
}

/**
 * Groups allowed to change the deal maturity date whatever the deal.
 * Mirror of OpportunityLiveDealRules::MATURITY_DATE_ALLOWED_GROUPS — the server enforces the
 * same list, this one only decides whether the pencil is drawn.
 */
export const DEAL_MATURITY_DATE_ALLOWED_GROUPS = ['superadmin', 'm_exec', 'busdev_manager'] as const;

/** `a;b;c` -> ['a', 'b', 'c'], blanks dropped. `exec_in_charge` holds several slugs. */
function splitDealSeats(raw: any): string[] {
  return (raw ?? '')
    .toString()
    .split(';')
    .map((seat: string) => seat.trim())
    .filter((seat: string) => seat !== '');
}

/** The three seats of the "Deal's team" panel. Same values as Crm_deal_team_rules::SEAT_*. */
export type DealTeamSeat = 'busdev_in_charge' | 'exec_in_charge' | 'referent';

/**
 * Groups allowed to change every seat of every deal's team (2026-10-02: the Exec manager
 * staffs the deals, busdev and exec in charge included).
 * Mirror of Crm_deal_team_rules::MANAGER_GROUPS.
 */
export const DEAL_TEAM_MANAGER_GROUPS = ['superadmin', 'm_exec'] as const;

/**
 * Is this user one of the people allowed to change this seat, status aside?
 * - Exec manager or super admin: every seat;
 * - Business developer seat: the busdev in charge;
 * - Executive in charge / Referent seats: anyone already on the deal's team.
 *
 * Decides whether the pencil is drawn; a terminated deal still draws it, disabled with its
 * tooltip, like every other deal-header action. Full verdict: `dealTeamSeatBlockedReason`.
 */
export function isDealTeamSeatActor(
  opportunity: any,
  seat: DealTeamSeat,
  userSlug: string,
  userGroups: string[] = [],
): boolean {
  if (!opportunity) {
    return false;
  }

  const groups = (userGroups ?? []).map((group) => (group ?? '').toString().trim().toLowerCase());
  if (groups.some((group) => (DEAL_TEAM_MANAGER_GROUPS as readonly string[]).includes(group))) {
    return true;
  }

  const slug = (userSlug ?? '').toString().trim();
  if (slug === '') {
    return false;
  }

  const busdev = splitDealSeats(opportunity.slug_busdev_in_charge);
  if (seat === 'busdev_in_charge') {
    return busdev.includes(slug);
  }

  return [
    ...busdev,
    ...splitDealSeats(opportunity.exec_in_charge),
    ...splitDealSeats(opportunity.supervisor),
  ].includes(slug);
}

/**
 * Why this user may not change this seat of the deal's team — '' when they may.
 * Exact mirror of Crm_deal_team_rules::edit_blocked_reason (the server refuses with the same sentence).
 */
export function dealTeamSeatBlockedReason(
  opportunity: any,
  seat: DealTeamSeat,
  userSlug: string,
  userGroups: string[] = [],
): string {
  if (!opportunity) {
    return 'The deal could not be found, so its team was not changed.';
  }

  const status = opportunity.slug_status ?? '';
  if (isDealTerminated(opportunity)) {
    return 'This deal is terminated: its team can no longer be changed.';
  }
  if (status === 'project_lost') {
    return 'This deal is lost: its team can no longer be changed.';
  }
  if (seat === 'referent' && status !== 'live_deal') {
    return 'A referent can only be assigned once the deal is live.';
  }

  if (isDealTeamSeatActor(opportunity, seat, userSlug, userGroups)) {
    return '';
  }

  if (seat === 'busdev_in_charge') {
    return 'Only the business developer in charge, an Exec manager or a super admin '
      + 'can change the business developer of this deal.';
  }

  const label = seat === 'exec_in_charge' ? 'executive in charge' : 'referent';
  return `Only a member of the deal's team, an Exec manager or a super admin can change the ${label} of this deal.`;
}

export function canEditDealTeamSeat(
  opportunity: any,
  seat: DealTeamSeat,
  userSlug: string,
  userGroups: string[] = [],
): boolean {
  return dealTeamSeatBlockedReason(opportunity, seat, userSlug, userGroups) === '';
}

const DEAL_TEAM_SEAT_LABELS: Record<DealTeamSeat, string> = {
  busdev_in_charge: 'Business developer',
  exec_in_charge: 'Executive in charge',
  referent: 'Referent',
};

/** Title of the error dialog when a seat could not be saved, e.g. "Executive in charge not saved". */
export function dealTeamSaveErrorTitle(seat: DealTeamSeat): string {
  return `${DEAL_TEAM_SEAT_LABELS[seat] ?? 'Deal team'} not saved`;
}

/**
 * Text of the error dialog when a seat could not be saved: the server sentence when there is one
 * (it names who may change the seat, or what failed), otherwise one that names the action — plus
 * the request reference the user can pass on to IT.
 *
 * @param error the HttpErrorResponse (or anything) the save failed with.
 */
export function dealTeamSaveErrorMessage(error: any, seat: DealTeamSeat): string {
  const body = error?.error;
  const serverMessage = typeof body?.message === 'string' ? body.message.trim() : '';
  const label = (DEAL_TEAM_SEAT_LABELS[seat] ?? 'deal team member').toLowerCase();

  let message = serverMessage
    || `The ${label} of this deal could not be saved. Your change was not saved; please try again.`;

  const requestId = (body?.request_id ?? '').toString().trim();
  if (requestId !== '') {
    message += ` (Reference: ${requestId})`;
  }

  return message;
}

/** Success toast after a seat was saved; says when the newcomers were notified. */
export function dealTeamSavedMessage(response: any, seat: DealTeamSeat): string {
  const label = DEAL_TEAM_SEAT_LABELS[seat] ?? 'Deal team';
  const notified = Array.isArray(response?.notified) ? response.notified.length : 0;
  if (notified === 0) {
    return `${label} updated.`;
  }

  return notified === 1
    ? `${label} updated. The new assignee has been notified.`
    : `${label} updated. The ${notified} new assignees have been notified.`;
}

/**
 * Maturity date edition (DB column `latest_maturity_date`, "Maturity date" in the Key dates band).
 *
 * Not gated by the deal status the way the CRM closing date is: the maturity is a contractual
 * date that keeps being corrected while the deal is live, which is exactly when it moves. Only a
 * terminated deal closes it, because a terminated deal is read-only everywhere.
 *
 * Five actors: the deal's own Exec in charge and Busdev in charge, plus an Exec manager
 * (`m_exec`), a Busdev manager (`busdev_manager`) or a super admin.
 *
 * @see OpportunityLiveDealRules::validateMaturityDateUpdate — same rule, server side.
 */
export function canEditDealMaturityDate(
  opportunity: any,
  userSlug: string,
  userGroups: string[] = [],
): boolean {
  if (!opportunity || isDealTerminated(opportunity)) {
    return false;
  }

  const groups = (userGroups ?? []).map((group) => (group ?? '').toString().trim().toLowerCase());
  if (groups.some((group) => (DEAL_MATURITY_DATE_ALLOWED_GROUPS as readonly string[]).includes(group))) {
    return true;
  }

  const slug = (userSlug ?? '').toString().trim();
  if (slug === '') {
    return false;
  }

  return (
    splitDealSeats(opportunity.exec_in_charge).includes(slug) ||
    splitDealSeats(opportunity.slug_busdev_in_charge).includes(slug)
  );
}

/** Why the maturity date is read-only — shown as the tooltip of the date when no pencil is drawn. */
export function getMaturityDateReadOnlyMessage(
  opportunity: any,
  userSlug: string,
  userGroups: string[] = [],
): string {
  if (canEditDealMaturityDate(opportunity, userSlug, userGroups)) {
    return '';
  }

  if (isDealTerminated(opportunity)) {
    return 'This deal is terminated. All deal data is read-only.';
  }

  return 'Only the Exec in charge, the Busdev in charge, an Exec manager, a Busdev manager, '
    + 'or a super admin can change the maturity date.';
}

export function canEditOpportunityBilling(
  opportunity: any,
  context: BillingEditContext = 'execution',
): boolean {
  if (isDealTerminated(opportunity)) {
    return false;
  }

  const status = opportunity?.slug_status ?? '';
  if (context === 'crm' || context === 'billing') {
    return (CRM_BILLING_EDITABLE_STATUSES as readonly string[]).includes(status);
  }

  return status === 'live_deal';
}

export function getBillingReadOnlyMessage(
  opportunity: any,
  context: BillingEditContext = 'execution',
): string {
  if (isDealTerminated(opportunity)) {
    return 'This deal is terminated. Billing is read-only.';
  }

  if (canEditOpportunityBilling(opportunity, context)) {
    return '';
  }

  if (context === 'crm' || context === 'billing') {
    return 'Billing is read-only for this deal status (editable when awaiting reply, won, or live).';
  }

  return 'The deal needs to be in live deal status.';
}

export function hasBillingInformation(opportunity: any): boolean {
  const billingCorporates = opportunity?.billing_corporates;

  if (Array.isArray(billingCorporates)) {
    return billingCorporates.length > 0;
  }

  if (typeof billingCorporates === 'string') {
    const normalized = billingCorporates.trim().toLowerCase();
    return normalized !== '' && normalized !== 'null';
  }

  return false;
}

/** The two pages that open a deal from its slug: the execution deal synopsis, the CRM opportunity wizard. */
export type DealPageContext = 'execution' | 'crm';

/**
 * The only two statuses the execution deal synopsis has anything to draw for.
 *
 * `live_deal` is the deal it manages; a terminated deal is the same deal, read-only. Everything
 * else is still in the CRM funnel: no tranche, no registre, no calendar, no payment notice — the
 * page used to render its whole shell around nothing.
 *
 * `project_closed` is not listed here: termination is read from `isDealTerminated()`, which also
 * honours the `is_closed` flag, so the two never disagree.
 */
const EXECUTION_SYNOPSIS_STATUSES = ['live_deal'] as const;

/**
 * Why a deal page has nothing to draw.
 *
 *  - `deleted`: the detail endpoint answered with no row. `crm_opportunity` is soft-deleted
 *    (`is_deleted`) and `Crm_opportunity_model::get_crm_opportunity_detail_by_slug` filters those
 *    rows out, so a deleted deal AND an unknown slug both come back as `null` on an HTTP 200.
 *    The client cannot tell the two apart, hence a single message covering both.
 *  - `lost`: execution only. A lost opportunity is the end of the funnel, not a step in it, so it
 *    is named on its own rather than lumped in with the deals still on their way.
 *  - `not_in_execution`: execution only. Awaiting reply, awaiting additional information, or won
 *    but not yet live — and any status added later, since the rule lists what execution OWNS
 *    rather than what it refuses.
 *
 * Both execution reasons keep the CRM wizard open: it is the page that owns those deals, and for
 * a lost one it is the only place it can be re-opened.
 */
export type DealPageUnavailableReason = 'deleted' | 'lost' | 'not_in_execution';

export interface DealPageUnavailableNotice {
  reason: DealPageUnavailableReason;
  title: string;
  message: string;
}

/**
 * The notice to show in place of the page, or `null` when the deal can be displayed.
 *
 * Call it only once the detail request has settled: before that, `opportunity` is undefined for
 * the ordinary reason that nothing has arrived yet, and it would read as `deleted`.
 */
export function dealPageUnavailableNotice(
  opportunity: any,
  context: DealPageContext,
): DealPageUnavailableNotice | null {
  if (!opportunity) {
    return {
      reason: 'deleted',
      title: context === 'execution' ? 'This deal is not available' : 'This opportunity is not available',
      message: context === 'execution'
        ? 'It has been deleted, or this link points to a deal that no longer exists.'
        : 'It has been deleted, or this link points to an opportunity that no longer exists.',
    };
  }

  // The CRM wizard owns every status, including lost: nothing to replace there.
  if (context !== 'execution') {
    return null;
  }

  const status = opportunity.slug_status ?? '';
  if ((EXECUTION_SYNOPSIS_STATUSES as readonly string[]).includes(status) || isDealTerminated(opportunity)) {
    return null;
  }

  if (status === 'project_lost') {
    return {
      reason: 'lost',
      title: 'This deal was lost',
      message: 'A lost opportunity never reaches execution, so it has no deal synopsis. '
        + 'Open it in the CRM to review it or to re-open it.',
    };
  }

  return {
    reason: 'not_in_execution',
    title: 'This deal has not reached execution yet',
    message: 'The deal synopsis only opens a live or a terminated deal. '
      + 'This one is still in the CRM funnel — open it there to follow it.',
  };
}

/**
 * Message of the error dialog shown when "mark as won" is refused. The backend message names the
 * deal and lists what to fill in (one bullet line each); it is shown as is. Without one (network
 * failure, unexpected response), a sentence naming the action is built instead.
 */
export function projectWonErrorMessage(error: any, dealName?: string | null): string {
  const backendMessage = error?.error?.message;
  if (typeof backendMessage === 'string' && backendMessage.trim() !== '') {
    return backendMessage.trim();
  }

  const name = (dealName ?? '').trim();
  const subject = name !== '' ? `The deal "${name}"` : 'The deal';
  const requestId = error?.headers?.get?.('X-Request-Id');
  const reference = requestId ? ` (reference: ${requestId})` : '';
  return `${subject} could not be marked as won: the server did not answer. Your change was not saved. Please try again, or contact IT${reference}.`;
}
